<template>
  <Teleport to="body">
    <UModal
      :open="isOpen"
      @update:open="
        (v) => {
          if (!v) close();
        }
      "
      :title="$t('patients.search_palette.title')"
      :description="$t('patients.search_palette.description')"
      :close="false"
      :ui="{
        overlay: 'bg-gray-900/50 backdrop-blur-sm grid place-items-start pt-[15vh] px-4',
        content:
          '!py-0 fixed left-1/2 top-1/2 z-50 -translate-x-1/2 w-full max-w-2xl rounded-2xl shadow-2xl ring-1 ring-gray-200 bg-white/95 backdrop-blur-xl flex flex-col focus:outline-none overflow-hidden',
      }"
    >
      <template #content>
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl">
          <div class="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
            <UIcon
              name="i-heroicons-magnifying-glass"
              class="h-5 w-5 shrink-0 text-gray-400"
            />
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              :placeholder="$t('patients.search_palette.placeholder')"
              class="w-full bg-transparent text-lg text-gray-900 placeholder-gray-400 outline-hidden"
              autofocus
              data-cy="search-palette-input"
              @keydown.esc="close"
              @keydown.enter="handleEnter"
            />
            <div
              v-if="loading"
              class="shrink-0"
            >
              <UIcon
                name="i-heroicons-arrow-path"
                class="h-5 w-5 animate-spin text-gray-400"
              />
            </div>
            <kbd
              v-else
              class="hidden shrink-0 rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-400 sm:block"
            >
              esc
            </kbd>
          </div>

          <div
            v-if="shouldFetch || patients.length > 0"
            class="max-h-[50vh] overflow-y-auto overscroll-contain"
          >
            <UEmpty
              v-if="patients.length === 0 && !loading && shouldFetch"
              variant="naked"
              icon="i-heroicons-user-group"
              :description="$t('patients.search_palette.no_results')"
            >
              <template
                v-if="canCreatePatient"
                #actions
              >
                <UButton
                  data-cy="search-palette-create-patient"
                  @click="navigateToCreatePatient"
                >
                  {{ $t("patients.search_palette.create_patient") }}
                </UButton>
              </template>
            </UEmpty>

            <div
              v-else-if="patients.length > 0"
              class="py-2"
            >
              <div class="px-4 pt-1 pb-2">
                <span class="text-xs font-medium tracking-wide text-gray-400 uppercase"> Patiënten </span>
              </div>
              <div
                v-for="patient in patients"
                :key="patient.id"
                class="group mx-2 cursor-pointer rounded-xl px-3 py-2.5 transition-all hover:bg-gray-100"
                data-cy="search-palette-result"
                @click="selectPatient(patient)"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-blue-500 to-blue-600 font-semibold text-sm text-white shadow-xs"
                  >
                    {{ getInitials(patient.full_name) }}
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium text-gray-900">
                      {{ patient.full_name }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ patient.dob ? $dayjs(patient.dob).format("L") : "-" }}
                    </p>
                  </div>
                  <UIcon
                    name="i-heroicons-arrow-right"
                    class="h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="patients.length > 0"
            class="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-5 py-2.5"
          >
            <div class="flex items-center gap-3 text-xs text-gray-400">
              <span class="flex items-center gap-1.5">
                <kbd class="rounded-sm bg-gray-200/80 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-500"
                  >↵</kbd
                >
                openen
              </span>
              <span class="flex items-center gap-1.5">
                <kbd class="rounded-sm bg-gray-200/80 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-500"
                  >esc</kbd
                >
                sluiten
              </span>
            </div>
            <span class="text-xs text-gray-400">
              {{ patients.length }} {{ patients.length === 1 ? "resultaat" : "resultaten" }}
            </span>
          </div>
        </div>
      </template>
    </UModal>
  </Teleport>
</template>

<script setup lang="ts">
import _ from "lodash";
import { Permission } from "~/enums/permission";
import { type PatientReference } from "~/types/api.d";

const { isOpen, close } = useSearchPalette();
const { isGrantedForPermission } = await useAuthorization();
const api = await useApi();

const searchQuery = ref("");
const patients = ref<PatientReference[]>([]);
const loading = ref(false);

const canCreatePatient = await isGrantedForPermission(Permission.PatientsInvite);

const shouldFetch = computed(() => searchQuery.value && searchQuery.value.trim().length >= 3);

const getInitials = (name: string): string => {
  return name
    .split(" ")
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
};

const fetchPatients = async (query: string) => {
  if (!query || query.trim().length < 3) {
    patients.value = [];
    return;
  }

  loading.value = true;
  try {
    const response = await api.patients(
      { number: 1, size: 10 },
      [{ column: "name", direction: "asc" }],
      [],
      [{ column: "search", value: query }],
    );
    patients.value = response.data;
  } catch {
    patients.value = [];
  } finally {
    loading.value = false;
  }
};

const debouncedFetch = _.debounce((query: string) => {
  fetchPatients(query);
}, 500);

watch(searchQuery, (newValue) => {
  if (newValue.trim().length < 3) {
    patients.value = [];
    loading.value = false;
    return;
  }
  loading.value = true;
  debouncedFetch(newValue);
});

watch(isOpen, (newValue) => {
  if (newValue) {
    searchQuery.value = "";
    patients.value = [];
  }
});

const selectPatient = (patient: PatientReference) => {
  close();
  navigateTo(`/patients/${patient.id}/`);
};

const handleEnter = () => {
  if (patients.value.length === 1) {
    selectPatient(patients.value[0]);
  }
};

const navigateToCreatePatient = () => {
  close();
  navigateTo("/patients/invitation");
};
</script>
